import { Signal } from "signals";
import { MenuSection } from "slotworks/components/betbar/menus/menu-section";
import AutoplayOptions from "slotworks/components/betbar/vo/autoplay-options";
import RadioGroup from "slotworks/components/ui/radio-group";
import SliderComponent from "slotworks/components/ui/slider-component";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import { InputTextEvent } from "slotworks/graphics/elements/flexi-input-text";
import FlexiInputText from "slotworks/graphics/elements/flexi-input-text";
import { ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import FlexiToggle from "slotworks/graphics/elements/flexi-toggle";
import Layers from "slotworks/graphics/layers/layers";
import SettingsModel from "slotworks/model/settings/settings-model";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

export default class AutoplaySection extends MenuSection {

    public startSignal: Signal;

    protected options: AutoplayOptions;

    private quickModeButton: FlexiButton;
    private fullModeButton: FlexiButton;

    private startButton: FlexiButton;

    private autoplaySlider: SliderComponent;
    private autoPlaysRadioGroup: RadioGroup<number>;

    private lossLimitInput: FlexiInputText;
    private singleWinLimitInput: FlexiInputText;
    private totalWinLimitInput: FlexiInputText;

    private stopOnFeatureWinToggle: FlexiToggle;
    private stopOnAnyWinToggle: FlexiToggle;
    private stopOnJackpotWinToggle: FlexiToggle;

    private lossLimitFlasher: TweenJS.Tween;
    private lossLimitFlashTimer: number;

    constructor(signal: Signal, initialValues: AutoplayOptions, layer: Layers = Layers.MenusContent) {
        super(signal, initialValues, layer);

        this.startSignal = new Signal();

        if (SettingsModel.isUKJurisdiction()) {
            this.setFullMode();
        } else {
            this.setQuickMode();
        }
    }

    public destroy() {
        this.reset();

        this.startSignal.removeAll();
        this.startSignal = null;

        super.destroy();
    }

    private init() {
        // Slider
        const flexiSlider = this.layer.getFlexiSlider("autoplays");
        const sliderLabel = this.layer.getFlexiText("autoplaysLabel");
        const minSliderLabel = this.layer.getFlexiText("autoplaysMinLabel");
        const maxSliderLabel = this.layer.getFlexiText("autoplaysMaxLabel");

        if (flexiSlider) {
            const steps = [0, ...this.options.autoplaySteps];
            this.autoplaySlider = new SliderComponent(flexiSlider, sliderLabel, minSliderLabel, maxSliderLabel);
            this.autoplaySlider.formatFunction = (value: number) => steps[value] ? steps[value].toString() : "0";

            const sliderValue = steps.indexOf(this.options.autoplays);

            this.autoplaySlider.setMin(0);
            this.autoplaySlider.setMax(steps.length - 1);
            this.autoplaySlider.setValue(sliderValue);

            this.autoplaySlider.onChange.add(this.setAutoplayIndex);
            this.setAutoplayIndex(sliderValue);
        }

        // Value radio selector
        const radioButtons = [];
        const radioLabels = [];

        do {
            const n = radioButtons.length + 1;
            const radioButton = this.layer.getFlexiButton("auto_val_" + n);
            const radioButtonLabel = this.layer.getFlexiText("auto_val_" + n + "_label");

            if (!radioButton) {
                break;
            } else {
                radioButtons.push(radioButton);
                radioLabels.push(radioButtonLabel);
            }
        } while (true);

        if (radioButtons.length > 0) {
            const prevButton = this.layer.getFlexiButton("previous");
            const nextButton = this.layer.getFlexiButton("next");
            const defaultStep = this.options.autoplays;
            this.autoPlaysRadioGroup = new RadioGroup(radioButtons, this.options.autoplaySteps, defaultStep, radioLabels, prevButton, nextButton);
            this.autoPlaysRadioGroup.onChange.add(this.setAutoplays);
            this.setAutoplays(defaultStep);
        }

        // Input texts
        this.lossLimitInput = this.layer.getFlexiInputText("lossLimit");
        if (this.lossLimitInput) {
            this.lossLimitInput.setDefaults("", 0);

            this.lossLimitInput.setValue(this.options.lossLimit / 100);

            this.lossLimitInput.on(InputTextEvent.CHANGE, this.setLossLimit);
        }

        this.singleWinLimitInput = this.layer.getFlexiInputText("singleWinLimit");
        if (this.singleWinLimitInput) {
            this.singleWinLimitInput.setDefaults("", 0);

            this.singleWinLimitInput.setValue(this.options.singleWinLimit / 100);

            this.singleWinLimitInput.on(InputTextEvent.CHANGE, this.setSingleWinLimit);
        }

        this.totalWinLimitInput = this.layer.getFlexiInputText("totalWinLimit");
        if (this.totalWinLimitInput) {
            this.totalWinLimitInput.setValue(this.options.totalWinLimit / 100);

            this.totalWinLimitInput.on(InputTextEvent.CHANGE, this.setTotalWinLimit);
        }

        // Toggles
        this.stopOnFeatureWinToggle = this.layer.getFlexiToggle("stopOnFeatureWin");
        if (this.stopOnFeatureWinToggle) {
            this.stopOnFeatureWinToggle.setEnabled(true);
            this.stopOnFeatureWinToggle.setValue(this.options.stopOnFeatureWin);

            this.stopOnFeatureWinToggle.on(ToggleEvent.CHANGE, this.setStopOnFeatureWin);
        }

        this.stopOnAnyWinToggle = this.layer.getFlexiToggle("stopOnAnyWin");
        if (this.stopOnAnyWinToggle) {
            this.stopOnAnyWinToggle.setEnabled(true);
            this.stopOnAnyWinToggle.setValue(this.options.stopOnAnyWin);

            this.stopOnAnyWinToggle.on(ToggleEvent.CHANGE, this.setStopOnAnyWin);
        }

        this.stopOnJackpotWinToggle = this.layer.getFlexiToggle("stopOnJackpotWin");
        if (this.stopOnJackpotWinToggle) {
            this.stopOnJackpotWinToggle.setEnabled(true);
            this.stopOnJackpotWinToggle.setValue(this.options.stopOnJackpotWin);

            this.stopOnJackpotWinToggle.on(ToggleEvent.CHANGE, this.setStopOnJackpotWin);
        }

        // Auto play menu type buttons
        this.quickModeButton = this.layer.getFlexiButton("quick");
        if (this.quickModeButton) {
            this.quickModeButton.on(ButtonEvent.POINTER_DOWN, this.setQuickMode);

            if (SettingsModel.isUKJurisdiction()) {
                this.quickModeButton.setEnabled(false);
                this.quickModeButton.setVisible(false);

                const quickLabel = this.layer.getFlexiText("quick_label");
                quickLabel.setVisible(false);
            }
        }

        this.fullModeButton = this.layer.getFlexiButton("full");
        if (this.fullModeButton) {
            this.fullModeButton.on(ButtonEvent.POINTER_DOWN, this.setFullMode);
        }

        // Start auto spins button
        this.startButton = this.layer.getFlexiButton("start");
        if (this.startButton) {
            this.startButton.on(ButtonEvent.POINTER_DOWN, this.start);
        }

        this.checkEnableStartButton();

        this.setupCloseCover();
        this.setupBackgroundBlocker();
    }

    private reset() {
        if (this.startButton) {
            this.startButton.off(ButtonEvent.POINTER_DOWN, this.start);
        }
        if (this.autoplaySlider) {
            this.autoplaySlider.onChange.remove(this.setAutoplayIndex);
        }
        if (this.autoPlaysRadioGroup) {
            this.autoPlaysRadioGroup.onChange.remove(this.setAutoplays);
        }
        if (this.lossLimitInput) {
            this.lossLimitInput.off(InputTextEvent.CHANGE, this.setLossLimit);
        }
        if (this.singleWinLimitInput) {
            this.singleWinLimitInput.off(InputTextEvent.CHANGE, this.setSingleWinLimit);
        }
        if (this.totalWinLimitInput) {
            this.totalWinLimitInput.off(InputTextEvent.CHANGE, this.setTotalWinLimit);
        }
        if (this.stopOnFeatureWinToggle) {
            this.stopOnFeatureWinToggle.off(ToggleEvent.CHANGE, this.setStopOnFeatureWin);
        }
        if (this.stopOnAnyWinToggle) {
            this.stopOnAnyWinToggle.off(ToggleEvent.CHANGE, this.setStopOnAnyWin);
        }
        if(this.stopOnJackpotWinToggle) {
            this.stopOnJackpotWinToggle.off(ToggleEvent.CHANGE, this.setStopOnJackpotWin);
        }
        if (this.quickModeButton) {
            this.quickModeButton.off(ButtonEvent.POINTER_DOWN, this.setQuickMode);
        }
        if (this.fullModeButton) {
            this.fullModeButton.off(ButtonEvent.POINTER_DOWN, this.setFullMode);
        }

        this.setLossLimitAttract(false);
    }

    private start = () => {
        this.startSignal.dispatch();
    }

    private setQuickMode = () => {
        if (this.layer.hasScene("quickautoplay")) {
            this.layer.setScene("quickautoplay").execute();

            this.reset();
            this.init();
        } else {
            this.setFullMode();
        }
    }

    private setFullMode = () => {
        this.layer.setScene("autoplay").execute();

        this.reset();
        this.init();
    }

    private setAutoplayIndex = (stepIndex: number) => {
        this.setAutoplays(stepIndex < 1 ? 0 : this.options.autoplaySteps[stepIndex - 1]);
    }

    private setAutoplays = (autoplays: number) => {
        this.options.autoplays = autoplays;

        this.checkEnableStartButton();
        this.checkLossLimitPrompt();

        this.sendOptions();
    }

    private setLossLimit = () => {
        this.options.lossLimit = this.lossLimitInput.getValue() * 100;

        this.checkLossLimitPrompt();

        this.sendOptions();
    }

    private setSingleWinLimit = () => {
        this.options.singleWinLimit = this.singleWinLimitInput.getValue() * 100;
        this.sendOptions();
    }

    private setTotalWinLimit = () => {
        this.options.totalWinLimit = this.totalWinLimitInput.getValue() * 100;
        this.sendOptions();
    }

    private setStopOnFeatureWin = () => {
        this.options.stopOnFeatureWin = this.stopOnFeatureWinToggle.getValue();
        this.sendOptions();
    }

    private setStopOnAnyWin = () => {
        this.options.stopOnAnyWin = this.stopOnAnyWinToggle.getValue();
        this.sendOptions();
    }

    private setStopOnJackpotWin = () => {
        this.options.stopOnJackpotWin = this.stopOnJackpotWinToggle.getValue();
        this.sendOptions();
    }

    private checkLossLimitPrompt() {
        const lossLimitSelected = this.options.lossLimit > 0;

        if (SettingsModel.isUKJurisdiction()) {
            const blockAutoplay = this.options.autoplays > 0 && !lossLimitSelected;
            this.setLossLimitAttract(blockAutoplay);
        }
        this.checkEnableStartButton();
    }

    private setLossLimitAttract(enabled: boolean) {
        if (this.lossLimitFlasher) {
            this.lossLimitFlasher.stop();
            Timer.clearTimeout(this.lossLimitFlashTimer);
            this.lossLimitInput.setHighlight(false);
        }

        if (enabled) {

            const tweenObj = {
                delta: 0
            };

            const flash = () => {
                this.lossLimitFlasher = new TweenJS.Tween(tweenObj)
                    .to({ delta: 1 }, 250)
                    .yoyo(true)
                    .repeat(3)
                    .onUpdate(() => {
                        if (!this.lossLimitInput.selected) {
                            this.lossLimitInput.setHighlight(tweenObj.delta > 0.5);
                        } else {
                            this.lossLimitInput.setHighlight(false);
                        }
                    })
                    .onComplete(() => {
                        this.lossLimitFlashTimer = Timer.setTimeout(flash, 2000);
                    })
                    .start();
            };

            flash();
        }
    }

    private checkEnableStartButton() {
        let enable = this.options.autoplays > 0;

        if (SettingsModel.isUKJurisdiction()) {
            const lossLimitSelected = this.options.lossLimit > 0;

            if (!lossLimitSelected) {
                enable = false;
            }
        }

        if (this.startButton) {
            this.startButton.setEnabled(enable);
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/betbar/menus/auto-play-section.ts